/**
 * Home's right rail: Calendar / Activity tabs with a collapse button. The
 * Activity tab carries the live dot and the entity filter in the tab bar and
 * renders the practice `ActivityRail` feed headerless underneath.
 */
import { PanelRightClose } from "lucide-react";
import { useState, type FC } from "react";

import { HeaderIconButton } from "@/components/app-header";
import {
  DEFAULT_ACTIVITY_FILTERS,
  type ActivityFilters
} from "@/features/clientProfile/tabs/activity/activityModel";
import { cn } from "@/lib/utils";

import {
  ActivityFilterMenu,
  ActivityLiveDot,
  ActivityRail
} from "../components/ActivityRail";
import type { DashboardScope } from "../types";
import { CalendarRailTab } from "./CalendarRailTab";

export type RailTab = "calendar" | "activity";

const TABS: ReadonlyArray<{ id: RailTab; label: string }> = [
  { id: "calendar", label: "Calendar" },
  { id: "activity", label: "Activity" }
];

export const RailTabs: FC<{
  tab: RailTab;
  onTab: (tab: RailTab) => void;
  onCollapse?: () => void;
  filters: ActivityFilters;
  onFilters: (next: ActivityFilters) => void;
}> = ({ tab, onTab, onCollapse, filters, onFilters }) => (
  <div
    className="flex h-10 shrink-0 items-center gap-0.5 border-b border-line-1 py-0 pr-1.5 pl-2"
    role="tablist"
    aria-label="Calendar and activity"
  >
    {TABS.map((t) => (
      <button
        key={t.id}
        type="button"
        role="tab"
        aria-selected={tab === t.id}
        onClick={() => onTab(t.id)}
        data-testid={`rail-tab-${t.id}`}
        className={cn(
          "inline-flex h-6.5 items-center gap-1.5 rounded-md px-2 text-base transition-colors outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
          tab === t.id
            ? "bg-fill-2 text-fg"
            : "text-fg-3 hover:bg-fill-1 hover:text-fg"
        )}
      >
        {t.id === "activity" ? <ActivityLiveDot /> : null}
        {t.label}
      </button>
    ))}
    <span className="flex-1" />
    {tab === "activity" ? (
      <ActivityFilterMenu filters={filters} onChange={onFilters} />
    ) : null}
    {onCollapse ? (
      <HeaderIconButton
        label="Hide calendar and activity"
        onClick={onCollapse}
        data-testid="rail-collapse"
      >
        <PanelRightClose className="size-4" />
      </HeaderIconButton>
    ) : null}
  </div>
);

type Props = {
  scope: DashboardScope;
  onCollapse?: () => void;
  className?: string;
  defaultTab?: RailTab;
};

export const RightRail: FC<Props> = ({
  scope,
  onCollapse,
  className,
  defaultTab = "calendar"
}) => {
  const [tab, setTab] = useState<RailTab>(defaultTab);
  const [filters, setFilters] = useState<ActivityFilters>(
    DEFAULT_ACTIVITY_FILTERS
  );
  return (
    <aside
      className={cn(
        "flex h-full min-h-0 w-85 shrink-0 flex-col border-l border-border bg-background",
        className
      )}
      data-testid="right-rail"
    >
      <RailTabs
        tab={tab}
        onTab={setTab}
        onCollapse={onCollapse}
        filters={filters}
        onFilters={setFilters}
      />
      {tab === "calendar" ? (
        <CalendarRailTab />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col pt-2">
          <ActivityRail
            source={scope}
            hideHeader
            filters={filters}
            onFiltersChange={setFilters}
          />
        </div>
      )}
    </aside>
  );
};
